import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Paper, useTheme, TextField, Typography, Switch, ThemeProvider, Backdrop, CircularProgress, CssBaseline } from "@mui/material";
import { Add, Cancel, Delete, Edit, Save, Visibility } from "@mui/icons-material";
import { CREATE_TESTCASE_URL, DELETE_TESTCASE_URL, GET_ALL_TESTCASES_URL, UPDATE_TESTCASE_URL } from "envConfig";
import { Logger } from "utils/Logger";
import { TestCase as TestCase_Structure } from "../../Interfaces/AutomateTest";


export const TestCase: React.FC = () => {
   const [testCases, setTestCases] = useState<TestCase_Structure[]>([]);
   const { tenantId, testSuiteId } = useParams();
   const [editMode, setEditMode] = useState<string | null>(null);
   const [testCaseEditedRow, setTestCaseEditedRow] = useState<Partial<TestCase_Structure>>({});
   const [newTestCaseRow, setnewTestCaseRow] = useState<TestCase_Structure | null>(null);
   const [loading, setLoading] = useState<boolean>(false);
   const navigate = useNavigate();
   const theme = useTheme();

// Fetch All testCases of TestSuite.

    const fetchTestCases = async () => {
      setLoading(true);
      try {
        const response = await fetch(GET_ALL_TESTCASES_URL+`/${testSuiteId}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
  
        const data = await response.json();
        setTestCases(data.data);
        setLoading(false);
        Logger.debug("st-ck-client/src/modules/AutomateTest/TestCase/TestCase.ts : fetchTestCases(): ","TestCases are: "+ JSON.stringify(data.data, null, 2));
      } catch (error) {
            setLoading(false);
            setTestCases([]);
            Logger.debug("st-ck-client/src/modules/AutomateTest/TestCase/TestCase.ts : fetchTestCases(): ", "Failed to fetch fetchTestCases:"+ error);
      }
    };

    useEffect(() => {
        fetchTestCases();
    }, []);


  const handleAddNewTestCaseRow = () => {
    const maxVersion = testCases
    .reduce((max, testCase) => Math.max(max, testCase.testCaseId), 0);
    const newTestCase: TestCase_Structure = {
        _id: "",
        name:"test",
        testCaseId: maxVersion + 1,
        description: "",
        runMode: true,
        status: "Pending",
        testSteps: [],
    };
    setnewTestCaseRow(newTestCase);
  };

  const handleInputChange = (_id: string, field: keyof TestCase_Structure, value: unknown) => {
    if (_id === "" && newTestCaseRow) {
      setnewTestCaseRow((prev) => prev ? { ...prev, [field]: value } : null);
    } else if (editMode === _id) {
      setTestCaseEditedRow((prev) => ({ ...prev, [field]: value }));
    } else {
      setTestCases((prev) =>
        prev.map((testCase) =>
            testCase._id === _id ? { ...testCase, [field]: value } : testCase
        )
      );
    }
  };

  const handleUpdateTestCase = async (_id:string) => {
    try {
      setLoading(true);
      const response = await fetch(`${UPDATE_TESTCASE_URL}/${testSuiteId}/${_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(testCaseEditedRow),
      });

      if (!response.ok) {
        setLoading(false);
        throw new Error(`Error: ${response.statusText}`);
      }

      setTestCases((prev) =>
        prev.map((testCase) =>
            testCase._id === testCaseEditedRow._id ? { ...testCase, ...testCaseEditedRow } : testCase
        )
      );
      setEditMode(null);
      setTestCaseEditedRow({});
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Logger.debug("st-ck-client/src/modules/AutomateTest/TestCase/TestCase.ts : handleUpdateTestCase(): ", "Failed to save testCase:" + error);
    }
  };

  const handleCancelEdit = () => {
    setnewTestCaseRow(null);
    setEditMode(null);
    setTestCaseEditedRow({});
  };

  const handleTestCaseEdit = (_id: string) => {
    setEditMode(_id);
    const testCase = testCases.find((testCase) => testCase._id === _id);
    if (testCase) setTestCaseEditedRow(testCase);
  };

  const handleDeleteTestCase = async(_id: string) => {
    try {
        setLoading(true);
        const response = await fetch(DELETE_TESTCASE_URL+`/${testSuiteId}/${_id}`, {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchTestCases();
        setTestCases((prevTestCase) => prevTestCase.filter((testCase) => testCase._id !== _id));
        setLoading(false);
      } catch (error) {
        setLoading(false);
        Logger.debug("st-ck-client/src/modules/AutomateTest/TestCase/TestCase.ts : handleDeleteTestCase(): ", "Failed to delete testCase:"+ error);
      }
  };

  const handleCreateNewTestCase = async () => {
    if (newTestCaseRow) {
      try {
        setLoading(true);
        const response = await fetch(CREATE_TESTCASE_URL+`/${testSuiteId}`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newTestCaseRow),
        });

        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchTestCases();
        Logger.debug("st-ck-client/src/modules/AutomateTest/TestCase/TestCase.ts : handleCreateNewTestCase(): ", "TestCase created Successfully.");

        setnewTestCaseRow(null);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        Logger.debug("st-ck-client/src/modules/AutomateTest/TestCase/TestCase.ts : handleCreateNewTestCase(): ", "Failed to save new TestCase: "+ error);
      }
    }
  };

  return (loading ?
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Backdrop
          sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </ThemeProvider>
      :
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1}  width="100%">
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="h4" gutterBottom>
        All TestCases
      </Typography>
        <Button variant="contained" sx={{backgroundColor: theme.palette.primary.main}} startIcon={<Add  />} onClick={handleAddNewTestCaseRow}>New TestCase</Button>
      </Box>
      <TableContainer component={Paper} sx={{maxHeight: "400px", overflow: "auto" }}>
        <Table sx={{ tableLayout: "auto", "& td, & th": { maxWidth: "150px", width:"150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}}>
          <TableHead>
            <TableRow>
              <TableCell>TC ID</TableCell>
              <TableCell>NAME</TableCell>
              <TableCell>DESCRIPTION</TableCell>
              <TableCell>RUN MODE</TableCell>
              <TableCell>TEST STEPS</TableCell>
              <TableCell>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testCases.map((testCase) => (
              <TableRow key={testCase._id}>
                <TableCell>{testCase.testCaseId}</TableCell>
                <TableCell>
                  {editMode === testCase._id ? (
                    <TextField
                      value={testCaseEditedRow.name || ""}
                      onChange={(e) => handleInputChange(testCase._id, "name", e.target.value)}
                    />
                  ) : (
                    testCase.name
                  )}
                </TableCell>
                <TableCell>
                  {editMode === testCase._id ? (
                    <TextField
                      value={testCaseEditedRow.description || ""}
                      onChange={(e) => handleInputChange(testCase._id, "description", e.target.value)}
                    />
                  ) : (
                    testCase.description
                  )}
                </TableCell>
                <TableCell>
                  {editMode === testCase._id ? (
                    <Switch
                      checked={Boolean(testCaseEditedRow.runMode)}
                      onChange={(e) => handleInputChange(testCase._id, "runMode", e.target.checked)}
                    />
                  ) : (
                    testCase.runMode ? "Yes" : "No"
                  )}
                </TableCell>
                <TableCell>{[testCase.testSteps]?.length || 0}
                </TableCell>
                <TableCell>
                {editMode === testCase._id ? (
                    <>
                      <IconButton onClick={()=>handleUpdateTestCase(testCase._id)}>
                        <Save />
                      </IconButton>
                      <IconButton onClick={handleCancelEdit}>
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <>
                  <IconButton onClick={() => navigate(`/tenant/${tenantId}/testSuite/${testSuiteId}/TestCases/${testCase._id}`)}>
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => handleTestCaseEdit(testCase._id)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteTestCase(testCase._id)}>
                    <Delete />
                  </IconButton>
                  </>
                )}
                </TableCell>
              </TableRow>
            ))}
            {newTestCaseRow && (
              <TableRow>
                <TableCell>{newTestCaseRow.testCaseId}</TableCell>
                <TableCell>
                  <TextField
                    value={newTestCaseRow.name}
                    onChange={(e) => handleInputChange("", "name", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={newTestCaseRow.description}
                    onChange={(e) => handleInputChange("", "description", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                   <TextField
                     value={newTestCaseRow.runMode}
                     onChange={(e) => handleInputChange("", "runMode", e.target.value)}
                  />
                  </TableCell>
                  <TableCell>{"0"}</TableCell>
                <TableCell>
                <TableCell>
                  <IconButton onClick={handleCreateNewTestCase}>
                    <Save />
                  </IconButton>
                  <IconButton onClick={handleCancelEdit}>
                    <Cancel />
                  </IconButton>
                </TableCell>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
